import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import API, { API_VERSION, REQUEST_METHOD } from '../../../api'
import { SubmissionReviewWorkflow } from '@app/pages/orders/SubmissionsTab/ReggoraReview/ReviewConstants'


export const initialState = {
  globalFields: [],
  repurchaseWarrantyReviews: {},
  repurchaseWarrantyReviewsResults: {},
  reviewNotes: {},
  reviewResults: {},
  rulesets: {
    isFetching: false,
    rulesets: [],
  },
  submissionReviews: {},
  // Perception evidence keyed by pipeline_run_id:
  // { [runId]: { status: 'loading'|'loaded'|'failed',
  //              byRef: { [photoId]: photo },
  //              roomGroupsByRef: { [roomGroupId]: group } } }
  // Room (photo) groups carry their member photos as expanded `children`, so a
  // room_group locus can resolve to a real photo tile.
  perceptionPhotos: {},
}


const reviewResultNames = [
  'repurchaseWarrantyReviewsResults',
  'reviewResults',
]

/**
 * Helper function to choose appropriate review results from reviewResultNames using a submissionReviewId
 * @param {Object} state - The current state
 * @param {string} submissionReviewId - The ID of the submission review
 * @returns {Object|null} The matching result object or null if not found
 */
export const getReviewResultsById = (state, submissionReviewId) => {
  if (!submissionReviewId) { return null }

  // Find the first property that contains results for this ID
  for (const property of reviewResultNames) {
    if (state[property]?.[submissionReviewId]) {
      return state[property][submissionReviewId]
    }
  }

  return null
}

/**
 * Helper function to update review results in any state property
 */
const updateReviewResultsInState = (state, submissionReviewResultId, payload) => {
  reviewResultNames.forEach(property => {
    if (state[property]?.[submissionReviewResultId]) {
      const existing = state[property][submissionReviewResultId]
      state[property] = {
        ...state[property],
        [submissionReviewResultId]: {
          ...existing,
          [payload._id]: payload,
        },
      }
    }
  })
}

export const fetchRulesets = createAsyncThunk('submissionReviews/fetchSubmissionRulesets', async (data, { rejectWithValue }) => {
  try {
    const { lenderId } = data

    const rulesets = await API(
      'submission-reviews/rule-sets/v1',
      API_VERSION.SERVICE,
      SERVICE_URL).request(REQUEST_METHOD.GET,
      {
        lender_id: lenderId,
      },
      {}
    )

    return rulesets.results
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

export const fetchReviewNotes = createAsyncThunk('submissionReviewNotes/fetchSubmissionReviewNotes', async (data, { rejectWithValue }) => {
  try {
    const { orderId, submissionVersion, reviewNoteType } = data
    const reviewNotes = await API(
      'submission-reviews/notes/v1',
      API_VERSION.SERVICE,
      SERVICE_URL).request(REQUEST_METHOD.GET,
      {
        order_id: orderId,
        submission_version: submissionVersion,
        review_note_type: reviewNoteType,
      },
      {}
    )
    if (reviewNotes.count === 0) {
      return rejectWithValue('No Notes')
    }
    return reviewNotes.results[0]
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

export const updateReviewNote = createAsyncThunk('submissionReviewNotes/saveSubmissionReviewNote', async (data, { rejectWithValue }) => {
  try {
    const { id, body, orderId, submissionVersion } = data

    const updateNoteResponse = await API(
      `submission-reviews/notes/v1/${id}`,
      API_VERSION.SERVICE,
      SERVICE_URL).request(REQUEST_METHOD.PUT,
      {},
      {
        _id: id,
        body: body,
      }
    )
    updateNoteResponse.orderId = orderId
    updateNoteResponse.submissionVersion = submissionVersion

    return updateNoteResponse
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

export const fetchSubmissionReviews = createAsyncThunk('submissionReviews/fetchSubmissionReviews', async (data, { rejectWithValue }) => {
  try {
    const { orderId, is_amc_lender } = data
    const submission_review_type = is_amc_lender ? 'amp' : 'lender'
    const submissionReviews = await API(
      'submission-reviews/v1',
      API_VERSION.SERVICE,
      SERVICE_URL).request(REQUEST_METHOD.GET,
      {
        order_id: orderId,
        submission_review_type: submission_review_type,
        submission_review_workflow: [SubmissionReviewWorkflow.POST_SUBMISSION],
      },
      {}
    )
    return submissionReviews
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})


/**
 * Fetches the repurchase warranty submission reviews.
 *
 * @param {Object} data - The data for the request.
 * @param {string} data.orderId - The ID of the order.
 */
export const fetchRepurchaseWarrantyReviews = createAsyncThunk('submissionReviews/fetchRepurchaseWarrantyReviews', async (data, { rejectWithValue }) => {
  try {
    const submissionReviews = await API(
      'submission-reviews/v1',
      API_VERSION.SERVICE,
      SERVICE_URL,
    ).request(REQUEST_METHOD.GET,
      {
        order_id: data.orderId,
        submission_review_type: 'repurchase_warranty',
        submission_review_workflow: [SubmissionReviewWorkflow.POST_SUBMISSION],
      },
      {}
    )
    return submissionReviews
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

export const fetchReviewResults = createAsyncThunk('submissionReviews/fetchReviewResults', async (data, { rejectWithValue }) => {
  try {
    const { result_id } = data
    const submissionReviews = await API(
      'submission-reviews/rule-results/v1',
      API_VERSION.SERVICE,
      SERVICE_URL,
    ).request(REQUEST_METHOD.GET,
      {
        submission_review_result_id: result_id,
        outcome: ['ERROR', 'PASS', 'FAIL', 'PREREQUISITE_FAIL', 'HARD_STOP', 'SOFT_STOP', 'NO_STOP', 'SKIP', 'MANUAL', 'ACCEPTED', 'REJECTED'],
        expand: ['data_points_by_submission', 'note_logs.user', 'outcome_override_logs.user'],
        limit: 9000,
      },
      {}
    )
    return submissionReviews
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

export const fetchRepurchaseWarrantyReviewResults = createAsyncThunk('submissionReviews/fetchRepurchaseWarrantyReviewResults', async (data, { rejectWithValue }) => {
  try {
    const { result_id, isExpandedWarranty = false } = data
    const outcomes = ['ERROR', 'HARD_STOP', 'NO_STOP', 'SOFT_STOP']

    if (isExpandedWarranty) {
      outcomes.push('ACCEPTED')
    }

    const submissionReviews = await API(
      'submission-reviews/rule-results/v1',
      API_VERSION.SERVICE,
      SERVICE_URL,
    ).request(REQUEST_METHOD.GET,
      {
        submission_review_result_id: result_id,
        outcome: outcomes,
        expand: ['data_points_by_submission', 'note_logs.user', 'outcome_override_logs.user'],
        limit: 0,
      },
      {}
    )
    return submissionReviews
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

export const fetchPerceptionPhotos = createAsyncThunk('submissionReviews/fetchPerceptionPhotos', async (data, { rejectWithValue }) => {
  const { runId } = data
  try {
    const response = await API(
      `submission-reviews/perception-evidence/v1/${runId}`,
      API_VERSION.SERVICE,
      SERVICE_URL,
    ).request(REQUEST_METHOD.GET,
      {
        // Photo tiles + the room (photo) groups that group-count rules point at.
        // Embed each group's member photos (crop + caption + bbox) so a
        // room_group locus resolves to a real tile, not an inert text link.
        include: ['photos', 'room_groups'],
        expand: ['room_groups.children'],
      },
      {}
    )
    return response
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
}, {
  // Dedupe concurrent fetches (several evidence grids mount in the same tick).
  // condition() runs BEFORE the pending action, so — unlike a getState check
  // inside the payload creator — it doesn't trip over its own 'loading' state.
  // Failed runs are allowed to retry.
  condition: ({ runId }, { getState }) => {
    const existing = getState().submissionReviews.perceptionPhotos?.[runId]
    if (existing && existing.status !== 'failed') {
      return false
    }
  },
})

export const fetchGlobalFields = createAsyncThunk('submissionReviews/fetchGlobalFields', async (data, { rejectWithValue }) => {
  try {
    const globalFields = await API('submission-reviews/global-fields/v1', API_VERSION.SERVICE, SERVICE_URL)
      .request(REQUEST_METHOD.GET,
        { limit: 1000 },
        {},
      )
    return globalFields
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

export const postRuleComment = createAsyncThunk('submissionReviews/postRuleComment', async (data, { rejectWithValue }) => {
  const { ruleId, note, user_id, user_type } = data
  try {
    const ruleComment = await API(`submission-reviews/rule-results/v1/${ruleId}/note`, API_VERSION.SERVICE, SERVICE_URL).request(REQUEST_METHOD.POST, {}, {
      note: note,
      user_id: user_id,
      user_type: user_type,
    })
    return ruleComment
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

export const postRuleOverride = createAsyncThunk('submissionReviews/postRuleOverride', async (data, { rejectWithValue, getState }) => {
  const {
    outcome,
    ruleId,
    submissionReviewId,
    user_id,
    user_type,
  } = data
  const currentState = getState()

  const rule = getReviewResultsById(currentState.submissionReviews, submissionReviewId)?.[ruleId]
  const version = Math.max(...rule?.outcome_override_logs?.map(o => o.version), 0)
  try {
    const ruleOverride = await API(
      `submission-reviews/rule-results/v1/${ruleId}/outcome`,
      API_VERSION.SERVICE,
      SERVICE_URL
    ).request(
      REQUEST_METHOD.POST,
      {
        expand: ['data_points_by_submission', 'outcome_override_logs.user'],
      },
      {
        current_version: version,
        outcome,
        user_id,
        user_type,
      }
    )
    return ruleOverride
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

/**
 * Standard-warranty AMP override (accept/reverse) routed through the monolith BFF.
 *
 * Unlike `postRuleOverride` (which writes outcome-overrides directly to the
 * submission-reviews service), this targets the dedicated lender_backend route
 * that writes the note + override AND recomputes warranty status synchronously,
 * so the banner can flip on the spot. Reason is required on accept (enforced by
 * the caller + server); optional on reverse.
 *
 * @param {Object} data
 * @param {string} data.orderId
 * @param {string} data.ruleId - rule-result id
 * @param {string} data.submissionReviewId - used to resolve the current override version
 * @param {number} data.submissionVersion
 * @param {string} data.outcome - e.g. 'ACCEPTED' (accept) or the engine outcome (reverse)
 * @param {string} [data.comment]
 * @returns {Object} { rule_result, repurchase_warranty }
 */
export const overrideWarrantyRuleResult = createAsyncThunk('submissionReviews/overrideWarrantyRuleResult', async (data, { rejectWithValue, getState }) => {
  const {
    orderId,
    ruleId,
    submissionReviewId,
    submissionVersion,
    outcome,
    comment,
  } = data
  const currentState = getState()
  const rule = getReviewResultsById(currentState.submissionReviews, submissionReviewId)?.[ruleId]
  const currentVersion = Math.max(...(rule?.outcome_override_logs?.map(o => o.version) || []), 0)

  try {
    const response = await API(
      `warranty/order/${orderId}/rule-result/${ruleId}/override/`,
      API_VERSION.TWO,
    ).request(
      REQUEST_METHOD.POST,
      {},
      {
        outcome,
        comment,
        submission_version: submissionVersion,
        current_version: currentVersion,
      }
    )
    // The API utility resolves 404s as { result: null } instead of rejecting.
    // Treat that as an error so the caller's catch handler fires.
    if (response?.result === null) {
      return rejectWithValue({ status: 404 })
    }
    return response
  } catch (err) {
    // Surface the raw error (carries `status` so callers can special-case 409 conflicts).
    return rejectWithValue(err)
  }
})

export const postSubmissionReview = createAsyncThunk('submissionReviews/submission-reviews/v1', async (data, { rejectWithValue }) => {
  const { orderId, submissionVersion, submissionReviewTypes } = data
  try {
    const submissionReviewPostCall = await API('submission-reviews/v1', API_VERSION.SERVICE, SERVICE_URL).request(REQUEST_METHOD.POST, {}, {
      order_id: orderId,
      submission_version: submissionVersion,
      submission_review_types: submissionReviewTypes,
    })
    return submissionReviewPostCall
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

export const postSubmissionReviewV2 = createAsyncThunk('submissionReviews/submission-reviews/v2', async (data, { rejectWithValue }) => {
  const { orderId, submissionVersion, submissionReviewType, ruleSetKey } = data
  try {
    const submissionReviewPostCall = await API('submission-reviews/v2', API_VERSION.SERVICE, SERVICE_URL).request(REQUEST_METHOD.POST, {}, {
      order_id: orderId,
      submission_version: submissionVersion,
      rule_set_key: ruleSetKey,
      submission_review_type: submissionReviewType,
      submission_review_workflow: 'post_submission',
    })
    return submissionReviewPostCall
  } catch (err) {
    return rejectWithValue(err.errors._error)
  }
})

const submissionReviewsSlice = createSlice({
  name: 'submissionReviews',
  initialState,
  reducers: {
    resetSubmissionReviews: () => {
      return initialState
    },
    toggleReviewNotes: (state, action) => {
      const { payload } = action
      const { orderId, submissionVersion } = payload

      const noteKey = `${orderId}-${submissionVersion}`
      const existing = state.reviewNotes[noteKey]

      const updatedNote = {
        ...existing,
        showNotes: !existing?.showNotes,
      }

      state.reviewNotes = {
        ...state.reviewNotes,
        [noteKey]: updatedNote,
      }
    },
  },
  extraReducers: {
    [fetchReviewResults.fulfilled]: (state, action) => {
      const { result_id } = action.meta.arg

      state.reviewResults = {
        ...state.reviewResults,
        [result_id]:
          action.payload.result
            .reduce(
              (acc, curr) => {
                acc[curr._id] = curr
                return acc
              }, {}
            ),
      }
    },
    [fetchReviewResults.rejected]: (state, action) => {
      console.log('todo fetch review result failed', action)
    },
    [fetchPerceptionPhotos.pending]: (state, action) => {
      const { runId } = action.meta.arg
      state.perceptionPhotos = {
        ...state.perceptionPhotos,
        [runId]: {
          ...(state.perceptionPhotos[runId] || {}),
          status: 'loading',
          byRef: state.perceptionPhotos[runId]?.byRef || {},
          roomGroupsByRef: state.perceptionPhotos[runId]?.roomGroupsByRef || {},
        },
      }
    },
    [fetchPerceptionPhotos.fulfilled]: (state, action) => {
      const { runId } = action.meta.arg
      const indexById = (items) => (items || []).reduce((acc, item) => {
        if (item?.id) {
          acc[item.id] = item
        }
        return acc
      }, {})
      state.perceptionPhotos = {
        ...state.perceptionPhotos,
        [runId]: {
          status: 'loaded',
          byRef: indexById(action.payload?.photos),
          roomGroupsByRef: indexById(action.payload?.room_groups),
        },
      }
    },
    [fetchPerceptionPhotos.rejected]: (state, action) => {
      // Thunks skipped by condition() reject with meta.condition — not a real
      // failure, so leave any existing entry untouched.
      if (action.meta?.condition) {
        return
      }
      const { runId } = action.meta.arg
      state.perceptionPhotos = {
        ...state.perceptionPhotos,
        [runId]: {
          ...(state.perceptionPhotos[runId] || {}),
          status: 'failed',
          byRef: state.perceptionPhotos[runId]?.byRef || {},
          roomGroupsByRef: state.perceptionPhotos[runId]?.roomGroupsByRef || {},
        },
      }
    },
    [fetchRepurchaseWarrantyReviewResults.fulfilled]: (state, action) => {
      const { result_id } = action.meta.arg

      state.repurchaseWarrantyReviewsResults = {
        ...state.repurchaseWarrantyReviewsResults,
        [result_id]:
          action.payload.result.reduce((acc, curr) => {
            acc[curr._id] = curr
            return acc
          }, {}),
      }
    },
    [fetchRepurchaseWarrantyReviewResults.rejected]: (state, action) => {
      console.log('todo fetch repurchase warranty review result failed', action)
    },
    [fetchRulesets.fulfilled]: (state, action) => {
      state.rulesets.rulesets = action.payload.map(ruleset => {
        return {
          _id: ruleset._id,
          key: ruleset.key,
          name: ruleset.rule_set_name,
          is_default: ruleset.is_default,
          date_modified: ruleset.date_modified,
        }
      })
      state.rulesets.isFetching = false
    },
    [fetchRulesets.pending]: (state, action) => {
      state.rulesets.isFetching = true
    },
    [fetchRulesets.rejected]: (state, action) => {
      state.rulesets.isFetching = false
    },
    [fetchReviewNotes.fulfilled]: (state, action) => {
      const { payload, meta } = action
      const { orderId, submissionVersion } = meta.arg

      const noteKey = `${orderId}-${submissionVersion}`

      state.reviewNotes = {
        ...state.reviewNotes,
        [noteKey]: {
          hasNotes: true,
          showNotes: false,
          note: { ...payload },
        },
      }
    },
    [fetchReviewNotes.rejected]: (state, action) => {
      const { meta } = action
      const { orderId, submissionVersion } = meta.arg
      const noteKey = `${orderId}-${submissionVersion}`

      state.reviewNotes = {
        ...state.reviewNotes,
        [noteKey]: {
          hasNotes: false,
          showNotes: false,
          note: null,
        },
      }
    },
    [updateReviewNote.rejected]: (state, action) => {
      console.warn('Review notes update failed')
    },
    [updateReviewNote.fulfilled]: (state, action) => {
      const { payload } = action
      const { orderId, submissionVersion } = payload

      const noteKey = `${orderId}-${submissionVersion}`
      const existing = state.reviewNotes[noteKey]

      const updatedNote = {
        ...existing,
        note: {
          ...existing.note,
          body: payload.body,
        },
      }
      state.reviewNotes = {
        ...state.reviewNotes,
        [noteKey]: updatedNote,
      }
    },
    [fetchSubmissionReviews.fulfilled]: (state, action) => {
      state.submissionReviews = {
        results: action.payload.results.map(res => {
          return {
            ...res,
            review_result: res.review_result,
          }
        }).reduce(
          (acc, curr) => {
            acc[curr._id] = curr
            return acc
          }, {}
        ),
        count: action.payload.count,
      }
    },
    [fetchRepurchaseWarrantyReviews.fulfilled]: (state, action) => {
      state.repurchaseWarrantyReviews = {
        results: action.payload.results.reduce((acc, res) => {
          acc[res._id] = {
            ...res,
            review_result: res.review_result,
          }
          return acc
        }, {}),
        reviewIdBySubmissionVersion: action.payload.results.reduce((acc, res) => {
          acc[res.submission_version] = res._id
          return acc
        }, {}),
        count: action.payload.count,
      }
    },
    [fetchGlobalFields.fulfilled]: (state, action) => {
      state.globalFields = action.payload?.results
    },
    [postRuleComment.fulfilled]: (state, action) => {
      const { payload } = action
      const insertionId = payload.submission_review_result_id
      updateReviewResultsInState(state, insertionId, payload)
    },
    [postRuleOverride.fulfilled]: (state, action) => {
      const { payload } = action
      const insertionId = payload.submission_review_result_id
      updateReviewResultsInState(state, insertionId, payload)
    },
    [overrideWarrantyRuleResult.fulfilled]: (state, action) => {
      // BFF returns { rule_result, repurchase_warranty }; refresh the rule card.
      const ruleResult = action.payload?.rule_result
      if (ruleResult) {
        updateReviewResultsInState(state, ruleResult.submission_review_result_id, ruleResult)
      }
    },
  },
})

const { actions, reducer } = submissionReviewsSlice
export const { toggleReviewNotes, resetSubmissionReviews } = actions

export default reducer
